import _ from "lodash";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import Breadcrumb from "../../components/Breadcrumb";
import CommentCard from "../../components/comments/CommentCard";
import ImageThumbnailGallery from "../../components/ImageThumbnailGallery";
import { PrevNextButtons, useNeighborNav } from "../../components/PrevNextNav";
import Spinner from "../../components/misc/Spinner";
import ConfirmDialog from "../../components/modal/ConfirmDialog";
import CountdownDialog from "../../components/modal/CountdownDialog";
import LinkWorkOrderDialog from "../../components/modal/LinkWorkOrderDialog";
import { SettingsContext } from "../../config/settings";
import { DEV_MODE } from "../../constants";
import * as deliveryNotesApi from "../../services/delivery_notes";
import * as usersApi from "../../services/users";
import { Role, useAuthStore } from "../../stores/auth";
import { DELIVERY_NOTE_ATTRS, DELIVERY_NOTE_STATE, useDeliveryNotesStore } from "../../stores/delivery_notes";
import { useToastStore } from "../../stores/toast";
import { useWorkOrdersStore } from "../../stores/work_orders";
import { formatDate, formatDateTime } from "../../utils/dateFormat";
import DeliveryNoteDetailsDocTable from "./DeliveryNoteDetailsDocTable";
import MobileActionBarContent from "../../components/mobile-action-bar/MobileActionBarContent";
import DeliveryNoteDetailsNoteTable from "./DeliveryNoteDetailsNoteTable";
import DeliveryNoteEmailModal from "./DeliveryNoteEmailModal";
import { TABS } from "./utils";

const withTabQuery = (path, tab) => (tab === TABS.NOTE ? path : `${path}?tab=${tab}`);

export default function DeliveryNoteDetails() {
  const { t } = useTranslation();
  const params = useParams();
  const noteId = Number.parseInt(params.noteId);
  const { whitelabel } = useContext(SettingsContext);
  const [queryParams, setQueryParams] = useSearchParams();
  const requestedTab = queryParams.get("tab") === TABS.DOCUMENTATION ? TABS.DOCUMENTATION : TABS.NOTE;

  const { me, hasRole } = useAuthStore(
    useShallow((state) => ({ me: state.me, hasRole: state.hasRole })),
  );
  const {
    byIds,
    allIds,
    lastFetchParams,
    neighbors,
    getById,
    fetchNeighbors,
    changeState,
    generatePdf,
    isInState,
    isOwner,
    addComment,
    updateComment,
    deleteComment,
    assignDeliveryNote,
    removeFromList,
  } = useDeliveryNotesStore(
    useShallow((state) => ({
      byIds: state.byIds,
      allIds: state.allIds,
      lastFetchParams: state.lastFetchParams,
      neighbors: state.neighbors,
      getById: state.getById,
      fetchNeighbors: state.fetchNeighbors,
      changeState: state.changeState,
      generatePdf: state.generatePdf,
      isInState: state.isInState,
      isOwner: state.isOwner,
      addComment: state.addComment,
      updateComment: state.updateComment,
      deleteComment: state.deleteComment,
      assignDeliveryNote: state.assignDeliveryNote,
      removeFromList: state.removeFromList,
    })),
  );
  const linkWorkOrder = useWorkOrdersStore((state) => state.linkDeliveryNote);

  const isLoading = !allIds.includes(noteId);
  const deliveryNote = byIds[noteId];

  const buildNoteUrl = useCallback(
    (id) => withTabQuery(`/delivery_notes/${id}/details`, requestedTab),
    [requestedTab],
  );

  const {
    prevId: prevNavId,
    nextId: nextNavId,
    navigatePrev,
    navigateNext,
  } = useNeighborNav({
    currentId: noteId,
    neighbors,
    buildUrl: buildNoteUrl,
  });
  const canNavigatePrev = prevNavId !== null;
  const canNavigateNext = nextNavId !== null;

  const isCreator = isOwner(deliveryNote, me);
  const isPerformer = !!deliveryNote && deliveryNote.performed_by?.id === me.id;
  const isAssignee = !!deliveryNote && deliveryNote.assigned_to?.id === me.id;
  const isInDraftedState = isInState(deliveryNote, DELIVERY_NOTE_STATE.DRAFTED);
  const isManager = hasRole(Role.ADMINISTRATOR) || hasRole(Role.MANAGER) || hasRole(Role.OFFICE);
  const canReview = !isInDraftedState && isManager;
  const canEdit =
    hasRole(Role.ADMINISTRATOR) ||
    ((hasRole(Role.MANAGER) || hasRole(Role.OFFICE)) && !isInDraftedState) ||
    ((isCreator || isPerformer || isAssignee) && isInDraftedState);

  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [showConfirmReview, setShowConfirmReview] = useState(false);
  const [showConfirmBilled, setShowConfirmBilled] = useState(false);
  const [showConfirmCanceled, setShowConfirmCanceled] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showConfirmCopy, setShowConfirmCopy] = useState(false);
  const [showLinkWorkOrder, setShowLinkWorkOrder] = useState(false);
  const [pendingAdvanceUrl, setPendingAdvanceUrl] = useState(null);
  const navigate = useNavigate();
  const addToast = useToastStore((state) => state.addToast);

  useEffect(() => {
    getById({ id: noteId });
    fetchNeighbors({ id: noteId });
    setPendingAdvanceUrl(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteId]);

  useEffect(() => {
    if (isManager) {
      usersApi.getAll().then(setAllUsers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setInvoiceNumber(deliveryNote?.invoice_number || "");
  }, [deliveryNote, noteId]);

  const listUrl = lastFetchParams
    ? `/delivery_notes?${new URLSearchParams({
        page: Math.floor(lastFetchParams.offset / lastFetchParams.limit),
        sort_by: lastFetchParams.sort_by,
        sort_dir: lastFetchParams.sort_dir,
        ...(lastFetchParams.filter && { filter: lastFetchParams.filter }),
        ...(lastFetchParams.q && { q: lastFetchParams.q }),
      })}`
    : "/delivery_notes";

  if (isLoading) {
    return (
      <div>
        <Breadcrumb
          items={[
            { label: t("delivery_notes.delivery_notes"), to: listUrl },
            { label: `${t("common.number_short")} …` },
          ]}
        >
          <PrevNextButtons
            canPrev={canNavigatePrev}
            canNext={canNavigateNext}
            onPrev={navigatePrev}
            onNext={navigateNext}
          />
        </Breadcrumb>
        <Spinner />
      </div>
    );
  }

  const customer = deliveryNote.customer;
  const hasCustomer =
    customer?.number ||
    customer?.name ||
    customer?.addresses?.some((a) => a.street || a.zip || a.city || a.district || a.state || a.country);
  const property = deliveryNote.customer_property;
  const hasProperty =
    property?.name ||
    property?.number ||
    property?.addresses?.some((a) => a.street || a.zip || a.city || a.district || a.state || a.country);
  const isReviewed = deliveryNote.is_reviewed;
  const isBilled = deliveryNote.is_billed;
  const isCanceled = deliveryNote.is_canceled;
  const hasDocumentation = deliveryNote.has_documentation;
  const tab = hasDocumentation ? requestedTab : TABS.NOTE;
  const editUrl = withTabQuery(`/delivery_notes/${deliveryNote.id}/edit`, tab);
  const notePaneClass = `delivery_note mb-4 tab-pane fade${tab === TABS.NOTE ? " active show" : ""}`;
  const documentationPaneClass = `card border-0 mb-4 tab-pane fade${tab === TABS.DOCUMENTATION ? " active show" : ""}`;

  const canLinkWorkOrder = isManager && !isCanceled && !deliveryNote.work_order;

  const showUpdatedBy =
    deliveryNote.updated_by && deliveryNote.updated_by.id !== deliveryNote.created_by.id;

  // Auto-advance skips notes another user is working on (assigned to someone else)
  const advanceTargetId =
    allIds.slice(allIds.indexOf(noteId) + 1).find((id) => {
      const candidate = byIds[id];
      return !candidate?.assigned_to || candidate.assigned_to.id === me.id;
    }) ?? null;

  const handleTabChange = (nextTab) => (e) => {
    e.preventDefault();
    if (nextTab === tab) return;
    const nextParams = new URLSearchParams(queryParams);
    if (nextTab === TABS.NOTE) nextParams.delete("tab");
    else nextParams.set("tab", nextTab);
    setQueryParams(nextParams);
  };

  const handlePdf = async () => {
    setGeneratingPdf(true);
    const result = await generatePdf({ id: deliveryNote.id });
    setGeneratingPdf(false);
    if (result.data) {
      const url = URL.createObjectURL(result.data);
      window.open(url, "_blank");
    }
  };

  const handleCopyLink = async () => {
    const result = await deliveryNotesApi.getShareLink({
      id: deliveryNote.id,
    });
    if (result.data?.url) {
      await navigator.clipboard.writeText(result.data.url);
      addToast("success", t("common.link_copied"));
    }
  };

  const handleLinkWorkOrder = async (workOrderId) => {
    const result = await linkWorkOrder({
      id: workOrderId,
      delivery_note_id: deliveryNote.id,
    });
    if (result.data) {
      setShowLinkWorkOrder(false);
      getById({ id: noteId });
    }
  };

  // ugly but works simple for now
  return (
    <div>
      <Breadcrumb
        items={[
          { label: t("delivery_notes.delivery_notes"), to: listUrl },
          { label: `${t("common.number_short")} ${deliveryNote.number}` },
        ]}
      >
        <div className="d-flex gap-2 align-items-center">
          <div className="btn-group d-none d-md-flex">
            <Link
              to={canEdit && !isReviewed ? editUrl : "#"}
              className={`btn btn-sm btn-white ${(!canEdit || isReviewed) && "disabled"}`}
            >
              <i className="fa fa-pencil t-plus-1 fa-fw fa-lg" /> {t("common.edit")}
            </Link>
            <button
              type="button"
              className="btn btn-sm btn-white"
              onClick={() => setShowConfirmCopy(true)}
            >
              <i className="fa fa-copy t-plus-1 fa-fw fa-lg" /> {t("common.copy")}
            </button>
            {isManager && (
              <>
                <button type="button" disabled={generatingPdf} className="btn btn-sm btn-white" onClick={handlePdf}>
                  <i className={`fa fa-${generatingPdf ? "spinner fa-spin" : "print"} t-plus-1 fa-fw fa-lg`} />{" "}
                  {t("common.print")}
                </button>
                <button type="button" className="btn btn-sm btn-white dropdown-toggle" data-bs-toggle="dropdown">
                  <i className="fa fa-caret-down" />
                </button>
                <div className="dropdown-menu dropdown-menu-end">
                  <button type="button" className="dropdown-item" onClick={() => setShowEmailModal(true)}>
                    <i className="fa fa-envelope fa-fw me-1" />
                    {t("common.send_email")}
                  </button>
                  <button type="button" className="dropdown-item" onClick={handleCopyLink}>
                    <i className="fa fa-link fa-fw me-1" />
                    {t("common.copy_link")}
                  </button>
                </div>
              </>
            )}
          </div>
          {isManager && isInDraftedState && (
            <button
              type="button"
              onClick={() => setShowConfirmCanceled(true)}
              className="btn btn-sm btn-white text-danger d-none d-md-inline-flex align-items-center"
            >
              <i className="fa fa-ban fa-fw me-1" />{t("delivery_notes.cancel")}
            </button>
          )}
          {canReview && !isReviewed && !isCanceled && (
            <button
              type="button"
              onClick={() => setShowConfirmReview(true)}
              className="btn btn-sm btn-success d-none d-md-inline-flex align-items-center"
            >
              <i className="fa fa-question-circle t-plus-1 fa-fw fa-lg" /> {t("delivery_notes.states.reviewed")}
            </button>
          )}
          {canReview && isReviewed && (
            <div className="btn-group d-none d-md-flex">
              {!isCanceled && (
                <div className="form-inline">
                  <input
                    className="form-control form-control-sm"
                    placeholder={t("invoices.number")}
                    disabled={isBilled || isCanceled}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    value={invoiceNumber}
                  />
                </div>
              )}
              <button
                type="button"
                onClick={() => setShowConfirmBilled(true)}
                disabled={isBilled || isCanceled || !invoiceNumber}
                className="btn btn-sm btn-success"
              >
                <i className={`fa fa-${isBilled ? "check" : "question-circle"} t-plus-1 fa-fw fa-lg`} />{" "}
                {isCanceled ? t("delivery_notes.states.canceled") : t("delivery_notes.states.billed")}
              </button>
              <button
                type="button"
                data-bs-toggle="dropdown"
                className="btn btn-sm btn-success dropdown-toggle"
                disabled={isBilled || isCanceled}
              >
                <i className="fa fa-caret-down" />
              </button>
              <div className="dropdown-menu dropdown-menu-end">
                <button
                  onClick={() => setShowConfirmCanceled(true)}
                  type="button"
                  className="dropdown-item"
                  disabled={isBilled || isCanceled}
                >
                  <i className={`fa fa-${isBilled ? "check" : "question-circle"} t-plus-1 fa-fw fa-lg`} />{" "}
                  {t("delivery_notes.cancel")}
                </button>
              </div>
            </div>
          )}
          <PrevNextButtons
            canPrev={canNavigatePrev}
            canNext={canNavigateNext}
            onPrev={navigatePrev}
            onNext={navigateNext}
          />
        </div>
      </Breadcrumb>

      <MobileActionBarContent>
        <div className="d-flex gap-2">
          <div className="btn-group flex-grow-1">
            <button
              type="button"
              className="btn btn-default"
              onClick={() => setShowConfirmCopy(true)}
            >
              <i className="fa fa-copy fa-fw me-1" />
              {t("common.copy")}
            </button>
            <Link
              to={canEdit && !isReviewed ? editUrl : "#"}
              className={`btn btn-success ${(!canEdit || isReviewed) && "disabled"}`}
            >
              <i className="fa fa-pencil fa-fw me-1" />
              {t("common.edit")}
            </Link>
          </div>
          <div className="btn-group">
            <button
              type="button"
              className="btn btn-default"
              disabled={!canNavigatePrev}
              onClick={navigatePrev}
            >
              <i className="fa fa-chevron-left" />
            </button>
            <button
              type="button"
              className="btn btn-default"
              disabled={!canNavigateNext}
              onClick={navigateNext}
            >
              <i className="fa fa-chevron-right" />
            </button>
          </div>
          <Link to={listUrl} className="btn btn-danger" title={t("common.close")}>
            <i className="fa fa-times" />
          </Link>
        </div>
      </MobileActionBarContent>

      <div className="row">
        <div className="col-12 col-lg-9 tab-content">
          {hasDocumentation && (
            <ul className="nav nav-pills mb-2 mx-2 mx-md-0">
              <li className="nav-item">
                <a
                  href="#tab-note"
                  className={`nav-link${tab === TABS.NOTE ? " active" : ""}`}
                  onClick={handleTabChange(TABS.NOTE)}
                >
                  <i className="fa fa-file-text fa-fw" />
                  <span className="ms-1">{t("delivery_notes.delivery_note")}</span>
                </a>
              </li>
              <li className="nav-item">
                <a
                  href="#tab-doc"
                  className={`nav-link${tab === TABS.DOCUMENTATION ? " active" : ""}`}
                  onClick={handleTabChange(TABS.DOCUMENTATION)}
                >
                  <i className="fa fa-book fa-fw" />
                  <span className="d-inline d-md-none ms-1">{t("menu.documentation_short")}</span>
                  <span className="d-none d-md-inline ms-1">{t("menu.documentation")}</span>
                </a>
              </li>
            </ul>
          )}
          <div className={notePaneClass}>
            <div className="delivery_note-company">
              <span>{whitelabel.name}</span>
            </div>
            <div className="row m-0 p-2 bg-light">
              {hasCustomer && (
                <div className="col-12 col-md p-0">
                  <small>
                    {t("customers.customer")}
                    {deliveryNote.customer.number && ` (Nr. ${deliveryNote.customer.number})`}
                  </small>
                  <div>{deliveryNote.customer.name}</div>
                  {deliveryNote.customer.addresses.map((address) => (
                    <address key={address.id}>
                      <span className="text-nowrap">{address.street}</span>
                      <br />
                      {!_.isEmpty(address.zip) && <>{address.zip}</>}
                      {!_.isEmpty(address.city) && <> {address.city}</>}
                      {!_.isEmpty(address.district) && (
                        <>
                          <br />
                          {address.district}
                        </>
                      )}
                      {!_.isEmpty(address.state) && (
                        <>
                          <br />
                          {address.state}
                        </>
                      )}
                      {!_.isEmpty(address.country) && (
                        <>
                          <br />
                          {address.country}
                        </>
                      )}
                    </address>
                  ))}
                </div>
              )}
              {hasProperty && (
                <div className="col-12 col-md p-0">
                  <small>
                    {t("customers.properties.property")}
                    {deliveryNote.customer_property.number && ` (Nr. ${deliveryNote.customer_property.number})`}
                  </small>
                  {!_.isEmpty(deliveryNote.customer_property.name?.trim()) && (
                    <div>{deliveryNote.customer_property.name}</div>
                  )}
                  {deliveryNote.customer_property.addresses.map((address) => {
                    const nameMatchesStreet = deliveryNote.customer_property.name?.trim() === address.street?.trim();
                    return (
                      <address key={address.id}>
                        {!!address.street && !nameMatchesStreet && (
                          <>
                            <span className="text-nowrap">{address.street}</span>
                            <br />
                          </>
                        )}
                        {!_.isEmpty(address.zip) && <>{address.zip}</>}
                        {!_.isEmpty(address.city) && <> {address.city}</>}
                        {!_.isEmpty(address.district) && (
                          <>
                            <br />
                            {address.district}
                          </>
                        )}
                        {!_.isEmpty(address.state) && (
                          <>
                            <br />
                            {address.state}
                          </>
                        )}
                        {!_.isEmpty(address.country) && (
                          <>
                            <br />
                            {address.country}
                          </>
                        )}
                      </address>
                    );
                  })}
                </div>
              )}
              <div className="col-12 col-md-auto p-0">
                <small>{t("delivery_notes.delivery_note")}</small>
                <table>
                  <tbody>
                    <tr>
                      <td className="pe-2">{t("delivery_notes.number")}:</td>
                      <td className="text-nowrap">{deliveryNote.number}</td>
                    </tr>
                    <tr>
                      <td className="pe-2">{t("customers.number")}:</td>
                      <td className="text-nowrap">{deliveryNote.customer.number}</td>
                    </tr>
                    <tr>
                      <td className="pe-2">{t("customers.properties.number")}:</td>
                      <td className="text-nowrap">{deliveryNote.customer_property.number}</td>
                    </tr>
                    <tr>
                      <td className="pe-2">{t("delivery_notes.date")}:</td>
                      <td>{formatDate(deliveryNote.delivered_at)}</td>
                    </tr>
                    <tr>
                      <td className="pe-2">{t("common.expert")}:</td>
                      <td className="text-nowrap">
                        {deliveryNote.performed_by ? (
                          <>
                            {deliveryNote.performed_by.name}
                            {deliveryNote.performed_by.number && (
                              <span className="text-muted"> ({deliveryNote.performed_by.number})</span>
                            )}
                          </>
                        ) : (
                          <span className="text-muted">{t("delivery_notes.no_performer")}</span>
                        )}
                      </td>
                    </tr>
                    {deliveryNote.coworkers?.map((coworker) => (
                      <tr key={coworker.id}>
                        <td className="pe-2">{t("common.expert")}:</td>
                        <td className="text-nowrap">
                          {coworker.name}
                          {coworker.number && <span className="text-muted"> ({coworker.number})</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="delivery_note-content">
              <div className="mt-4">
                <h6>Artikel</h6>
                <DeliveryNoteDetailsNoteTable deliveryNote={deliveryNote} />
              </div>
            </div>
            <div className="">
              {deliveryNote.remark !== "" && (
                <div className="row mb-3">
                  <div className="col">
                    <div className="text-dark">
                      <h5>{t("menu.remark")}</h5>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: deliveryNote.remark,
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
              {deliveryNote.files.length > 0 && (
                <div className="row mb-3">
                  <div className="col">
                    <div className="float-start">
                      <ImageThumbnailGallery
                        files={deliveryNote.files}
                        showFileDetails={false}
                      />
                    </div>
                  </div>
                </div>
              )}
              <div className="row mb-3">
                {!!deliveryNote.employee_signature && (
                  <div className="col">
                    <div className="text-dark">
                      <h5>
                        {t("customers.menu.signature")} {t("common.expert")}
                      </h5>
                      <img
                        src={deliveryNote.employee_signature}
                        className="mt-2"
                        style={{ maxWidth: "150px" }}
                        alt=""
                      />
                      {!!deliveryNote.employee_signature_name && (
                        <div className="mt-2">
                          <small>{deliveryNote.employee_signature_name}</small>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {!!deliveryNote.customer_signature && (
                  <div className="col text-dark">
                    <div className="float-end text-dark">
                      <h5>
                        {t("customers.menu.signature")} {t("customers.customer")}
                      </h5>
                      <img
                        src={deliveryNote.customer_signature}
                        className="mt-2"
                        style={{ maxWidth: "150px" }}
                        alt=""
                      />
                      {!!deliveryNote.customer_signature_name && (
                        <div className="mt-2">
                          <small>{deliveryNote.customer_signature_name}</small>
                        </div>
                      )}
                      {!!deliveryNote.customer_signature_date && (
                        <div>
                          <small className="text-muted">{formatDateTime(deliveryNote.customer_signature_date)}</small>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {!!whitelabel.pdf_footer_text && (
                <p className="text-center mb-5px fw-bold">{whitelabel.pdf_footer_text}</p>
              )}
              <p className="text-center">
                {!!whitelabel.url && (
                  <span className="me-10px">
                    <i className="fa fa-fw fa-lg fa-globe"></i> {whitelabel.url}
                  </span>
                )}
                {!!whitelabel.phone && (
                  <span className="me-10px">
                    <i className="fa fa-fw fa-lg fa-phone-volume"></i> {whitelabel.phone}
                  </span>
                )}
                {!!whitelabel.fax && (
                  <span className="me-10px">
                    <i className="fa fa-fw fa-lg fa-fax"></i> {whitelabel.fax}
                  </span>
                )}
                {!!whitelabel.email && (
                  <span className="me-10px">
                    <i className="fa fa-fw fa-lg fa-envelope"></i> {whitelabel.email}
                  </span>
                )}
              </p>
            </div>
          </div>
          {hasDocumentation && (
            <div className={documentationPaneClass}>
              <div className="card-header h6 mb-0 bg-none p-3">
                <i className="fa fa-book fa-lg fa-fw text-dark text-opacity-50 me-1" />
                {t("menu.documentation")}
              </div>
              <div className="card-body">
                <DeliveryNoteDetailsDocTable
                  schema={deliveryNote.documentation_schema}
                  documentation={deliveryNote.documentation}
                />
              </div>
            </div>
          )}
        </div>
        <div className="col-12 col-lg-3 mt-3 mt-lg-0">
          <div className="card border-0 mb-4">
            <div className="card-header h6 mb-0 bg-none p-3">{t("delivery_notes.audit")}</div>
            <div className="card-body">
              <div className={showUpdatedBy ? "mb-2" : ""}>
                <span className="text-muted">{t("common.created_by")}:</span> {deliveryNote.created_by.name}{" "}
                {t("common.on_date")} {formatDateTime(deliveryNote.created_at)}
              </div>
              {showUpdatedBy && (
                <div>
                  <span className="text-muted">{t("common.updated_by")}:</span> {deliveryNote.updated_by.name}{" "}
                  {t("common.on_date")} {formatDateTime(deliveryNote.updated_at)}
                </div>
              )}
            </div>
          </div>
          <div className="card border-0 mb-4">
            <div className="card-header h6 mb-0 bg-none p-3">{t("delivery_notes.assigned_to")}</div>
            <div className="card-body">
              {isManager ? (
                <div className="input-group">
                  <select
                    className="form-select"
                    disabled={isBilled || isCanceled}
                    value={deliveryNote.assigned_to?.id ?? ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      assignDeliveryNote({
                        id: deliveryNote.id,
                        assigned_to_id: value ? Number(value) : null,
                      });
                    }}
                  >
                    <option value="">{t("delivery_notes.unassigned")}</option>
                    {allUsers
                      .filter((u) => u.active)
                      .map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name}
                        </option>
                      ))}
                  </select>
                  <button
                    type="button"
                    className="btn btn-red"
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    title={t("delivery_notes.assign_to_me")}
                    disabled={isBilled || isCanceled || deliveryNote.assigned_to?.id === me.id}
                    onClick={() =>
                      assignDeliveryNote({
                        id: deliveryNote.id,
                        assigned_to_id: me.id,
                      })
                    }
                  >
                    <i className="fa fa-user" />
                  </button>
                </div>
              ) : (
                <p className="mb-0">
                  {deliveryNote.assigned_to ? deliveryNote.assigned_to.name : t("delivery_notes.unassigned")}
                </p>
              )}
            </div>
          </div>

          {(deliveryNote.work_order || canLinkWorkOrder) && (
            <div className="card border-0 mb-4">
              <div className="card-header h6 mb-0 bg-none p-3">
                <i className="fa fa-clipboard-list fa-lg fa-fw text-dark text-opacity-50 me-1" />
                {t("work_orders.work_order")}
              </div>
              <div className="card-body">
                {deliveryNote.work_order && (
                  <>
                    <Link
                      to={`/work_orders/${deliveryNote.work_order.id}/details`}
                      className="fw-bold me-2"
                    >
                      {deliveryNote.work_order.number}
                    </Link>
                    <span className="badge bg-default text-dark">
                      {t(`work_orders.states.${deliveryNote.work_order.status}`)}
                    </span>
                  </>
                )}
                {canLinkWorkOrder && (
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-white flex-fill flex-xxl-grow-0"
                      onClick={() => setShowLinkWorkOrder(true)}
                    >
                      <i className="fa fa-link fa-fw me-1" />
                      {t("work_orders.link")}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          <CommentCard
            key={noteId}
            comments={deliveryNote.comments || []}
            canManageComment={(comment) => me?.id === comment.created_by?.id || hasRole(Role.ADMINISTRATOR)}
            onAdd={(text) => addComment({ id: noteId, text })}
            onUpdate={(commentId, text) => updateComment({ id: noteId, commentId, text })}
            onDelete={(commentId) => deleteComment({ id: noteId, commentId })}
          />
        </div>
      </div>
      {showConfirmReview && (
        <ConfirmDialog
          title={t("delivery_notes.states.reviewed")}
          message={t("delivery_notes.menu.confirm_reviewed")}
          confirmLabel={t("delivery_notes.states.reviewed")}
          onCancel={() => setShowConfirmReview(false)}
          onConfirm={() => {
            setShowConfirmReview(false);
            changeState({
              id: deliveryNote.id,
              state: DELIVERY_NOTE_STATE.REVIEWED,
            });
          }}
        />
      )}
      {showConfirmBilled && (
        <ConfirmDialog
          title={t("delivery_notes.states.billed")}
          message={t("delivery_notes.menu.confirm_billed")}
          confirmLabel={t("delivery_notes.states.billed")}
          onCancel={() => setShowConfirmBilled(false)}
          onConfirm={async () => {
            setShowConfirmBilled(false);
            const result = await changeState({
              id: deliveryNote.id,
              state: DELIVERY_NOTE_STATE.BILLED,
              attrs: {
                [DELIVERY_NOTE_ATTRS.INVOICE_NUMBER]: invoiceNumber,
              },
            });
            if (result.data) {
              setPendingAdvanceUrl(advanceTargetId ? buildNoteUrl(advanceTargetId) : "/delivery_notes");
            }
          }}
        />
      )}
      {showEmailModal && (
        <DeliveryNoteEmailModal deliveryNote={deliveryNote} onClose={() => setShowEmailModal(false)} />
      )}
      {showConfirmCanceled && (
        <ConfirmDialog
          title={t("delivery_notes.cancel")}
          message={t("delivery_notes.menu.confirm_canceled")}
          confirmLabel={t("delivery_notes.cancel")}
          confirmClassName="btn btn-danger"
          onCancel={() => setShowConfirmCanceled(false)}
          onConfirm={async () => {
            setShowConfirmCanceled(false);
            await changeState({
              id: deliveryNote.id,
              state: DELIVERY_NOTE_STATE.CANCELED,
            });
          }}
        />
      )}
      {showConfirmCopy && (
        <ConfirmDialog
          title={t("common.copy")}
          message={t("delivery_notes.menu.confirm_copy")}
          confirmLabel={t("common.copy")}
          onCancel={() => setShowConfirmCopy(false)}
          onConfirm={() => {
            setShowConfirmCopy(false);
            navigate(
              `/delivery_notes/add?templateId=${deliveryNote.template.id}&copyFromId=${deliveryNote.id}`,
            );
          }}
        />
      )}
      {showLinkWorkOrder && (
        <LinkWorkOrderDialog
          onSelect={handleLinkWorkOrder}
          onClose={() => setShowLinkWorkOrder(false)}
        />
      )}
      {pendingAdvanceUrl && (
        <CountdownDialog
          message={t("delivery_notes.menu.advancing_next")}
          onComplete={() => {
            navigate(pendingAdvanceUrl);
            removeFromList(noteId);
          }}
          onCancel={() => setPendingAdvanceUrl(null)}
        />
      )}
      {DEV_MODE && (
        <pre
          className="m-t-xl"
          style={{
            fontSize: "1rem",
            padding: ".25rem .5rem",
            overflowX: "scroll",
          }}
        >
          delivery_note: {JSON.stringify(deliveryNote, null, 2)}
        </pre>
      )}
    </div>
  );
}
